// src/pages/AuthPage.jsx - Split Screen Design with Animations
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Eye, EyeOff, Zap, Target, Trophy, Users, TrendingUp, Award, BookOpen, ChevronRight, Crown, Lock as LockIcon, ShieldCheck, Check, Quote } from 'lucide-react';
import { getAssignedVariant, LANDING_PAGE_EXPERIMENT_ID } from '../utils/experiments';
import { auth } from '../firebase';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  signOut,
  confirmPasswordReset,
  sendEmailVerification
} from 'firebase/auth';
import { sendPasswordResetEmailCustom, sendVerificationEmail } from '../utils/authUtils';
import { initializeUserData, logUserActivity } from '../utils/activityLogger';
import { signInWithGoogleAndProcess } from '../utils/googleSignIn';
import { useSearchParams } from 'react-router-dom';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import LogamiLogoSpinner from '../components/ui/LogamiLogoSpinner';
import { trackAuthView, trackSignupStart, trackSignupComplete } from '../utils/conversionEvents';

const AuthPage = ({ onLogin, onRegister, onPlanGenerated }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [isLoginView, setIsLoginView] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('mode') !== 'signup';
    } catch {
      return true;
    }
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useLocalizedNavigate();
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [isPasswordResetMode, setIsPasswordResetMode] = useState(false);
  const [resetOobCode, setResetOobCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);

  const isVariantB = getAssignedVariant(LANDING_PAGE_EXPERIMENT_ID) === 'variant_b';

  useEffect(() => {
    const mode = searchParams.get('mode');
    const oobCode = searchParams.get('oobCode');
    
    if (mode === 'signup') {
      setIsLoginView(false);
    }
    
    // Check if this is a password reset link
    if (mode === 'resetPassword' && oobCode) {
      console.log('Password reset mode detected');
      setIsPasswordResetMode(true);
      setResetOobCode(oobCode);
      // Clear URL parameters for security
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [searchParams]);

  useEffect(() => {
    if (isPasswordResetMode) return;
    trackAuthView({ mode: isLoginView ? 'login' : 'signup' });
  }, [isLoginView, isPasswordResetMode]);


  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 6;
  };


  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    trackSignupStart({ method: 'email' });

    // variant_b: 2-field form, derive first name from email prefix, skip confirm password
    const resolvedFirstName = isVariantB
      ? (email.split('@')[0] || 'Student')
      : firstName.trim();
    const resolvedLastName = isVariantB ? '' : lastName.trim();

    if (!isVariantB && (!firstName.trim() || !lastName.trim())) {
      setError(t('auth.error.nameRequired'));
      setIsLoading(false);
      return;
    }

    if (!validateEmail(email)) {
      setError(t('auth.error.invalidEmail'));
      setIsLoading(false);
      return;
    }

    if (!validatePassword(password)) {
      setError(t('auth.error.passwordTooShort'));
      setIsLoading(false);
      return;
    }

    if (!isVariantB && password !== confirmPassword) {
      setError(t('auth.error.passwordMismatch'));
      setIsLoading(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      let verificationEmailSent = false;

      await updateProfile(user, {
        displayName: resolvedFirstName
      });

      await initializeUserData(user, {
        firstName: resolvedFirstName,
        lastName: resolvedLastName
      });

      // Auto-send verification email right after signup, but never block onboarding/dashboard.
      try {
        const verificationResult = await sendVerificationEmail(user);
        if (verificationResult?.success) {
          verificationEmailSent = true;
        } else {
          await sendEmailVerification(user, {
            url: `${window.location.origin}/`,
            handleCodeInApp: false,
          });
          verificationEmailSent = true;
        }
      } catch (verificationError) {
        console.error('Failed to auto-send verification email after signup:', verificationError);
      }

      if (verificationEmailSent) {
        try {
          sessionStorage.setItem('logami.signupVerificationEmailSent', 'true');
        } catch (storageError) {
          console.warn('Unable to persist signup verification banner flag:', storageError);
        }
      }

      await logUserActivity({
        action: 'signup',
        details: {
          verificationRequired: true,
          verificationEmailSent,
          registrationMethod: 'email'
        }
      });

      trackSignupComplete({ method: 'email', userId: user.uid });
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError(t('auth.error.emailInUse'));
      } else if (err.code === 'auth/weak-password') {
        setError(t('auth.error.weakPassword'));
      } else if (err.code === 'auth/invalid-email') {
        setError(t('auth.error.invalidEmail'));
      } else if (err.code === 'auth/operation-not-allowed') {
        setError(t('auth.error.operationNotAllowed'));
      } else {
        setError(t('auth.error.signupFailed', { message: err.message }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (onLogin) {
        await onLogin(email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }

      await logUserActivity({
        action: 'login',
        details: {
          loginMethod: 'email',
          timestamp: new Date()
        }
      });
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setError(t('auth.error.userNotFound'));
      } else if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setError(t('auth.error.wrongPassword'));
      } else if (err.code === 'auth/too-many-requests') {
        setError(t('auth.error.tooManyRequests'));
      } else {
        setError(err.message || t('auth.error.loginFailed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setIsLoading(true);
    try {
      await signInWithGoogleAndProcess();
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        // User dismissed the popup, not an error
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError(t('auth.error.googleAccountExists'));
      } else {
        setError(t('auth.error.googleFailed', { message: err.message }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const toggleAuthMode = () => {
    setIsLoginView(!isLoginView);
    setError('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFirstName('');
    setLastName('');
    setShowPasswordReset(false);
    setResetEmail('');
    setResetEmailSent(false);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!validateEmail(resetEmail)) {
      setError(t('auth.resetPassword.error.invalidEmail'));
      setIsLoading(false);
      return;
    }

    try {
      const result = await sendPasswordResetEmailCustom(resetEmail);
      if (result.success) {
        setResetEmailSent(true);
        await logUserActivity({
          action: 'password_reset_requested',
          details: {
            email: resetEmail,
            timestamp: new Date()
          }
        });
      } else {
        // Handle specific error cases
        if (result.error?.includes('Invalid email')) {
          setError(t('auth.resetPassword.error.invalidEmail'));
        } else {
          setError(t('auth.resetPassword.error.failed'));
        }
      }
    } catch (err) {
      console.error('Password reset error:', err);
      // Handle error from the callable function
      // Note: For security, we don't reveal if email exists or not
      if (err.message?.includes('Invalid email') || err.code === 'invalid-argument') {
        setError(t('auth.resetPassword.error.invalidEmail'));
      } else {
        // Generic error message for security
        setError(t('auth.resetPassword.error.failed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackFromPasswordReset = () => {
    setShowPasswordReset(false);
    setResetEmail('');
    setResetEmailSent(false);
    setError('');
    setIsLoginView(true);
  };

  const handleConfirmPasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!validatePassword(newPassword)) {
      setError(t('auth.resetPassword.error.passwordTooShort'));
      setIsLoading(false);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError(t('auth.error.passwordMismatch'));
      setIsLoading(false);
      return;
    }

    try {
      console.log('Confirming password reset with oobCode...');
      await confirmPasswordReset(auth, resetOobCode, newPassword);
      console.log('Password reset successful!');
      setPasswordResetSuccess(true);
      
      await logUserActivity({
        action: 'password_reset_completed',
        details: {
          timestamp: new Date()
        }
      });
    } catch (err) {
      console.error('Password reset confirmation error:', err);
      if (err.code === 'auth/expired-action-code') {
        setError(t('auth.resetPassword.error.expiredLink', 'This password reset link has expired. Please request a new one.'));
      } else if (err.code === 'auth/invalid-action-code') {
        setError(t('auth.resetPassword.error.invalidLink', 'This password reset link is invalid. Please request a new one.'));
      } else if (err.code === 'auth/weak-password') {
        setError(t('auth.error.weakPassword'));
      } else {
        setError(t('auth.resetPassword.error.failed'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ─── Variant B (warm stone/amber) auth render ─────────────────
  const renderVariantBContent = () => {
    const vbFeatures = [
      { title: t('auth.variantB.feature1.title'), desc: t('auth.variantB.feature1.desc') },
      { title: t('auth.variantB.feature2.title'), desc: t('auth.variantB.feature2.desc') },
      { title: t('auth.variantB.feature3.title'), desc: t('auth.variantB.feature3.desc') },
    ];
    const vbStats = [
      { value: '1,000+', label: t('auth.variantB.stats.students') },
      { value: '95%',    label: t('auth.variantB.stats.passRate') },
      { value: '4.9★',  label: t('auth.variantB.stats.rating') },
    ];
    const TEACHERS_MINI = [
      { initials: 'ES', photo: '/images/amir-safari.jpg' },
      { initials: 'MK', color: '#E8D5B7' },
      { initials: 'SL', color: '#C4B5A0' },
      { initials: 'AH', color: '#D4C4AA' },
    ];

    return (
      <div className="w-full max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_460px] gap-8 lg:gap-16 items-center min-h-screen py-12">

          {/* ── LEFT TRUST PANEL (desktop only) ───────────────── */}
          <div className="hidden lg:flex flex-col justify-center space-y-9 pr-4">
            {/* Course badge */}
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold tracking-wide uppercase">
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                {t('auth.variantB.badge')}
              </span>
            </div>

            {/* Heading */}
            <div className="space-y-4">
              <h1 className="font-space text-5xl xl:text-6xl font-black text-stone-900 leading-[1.08]">
                {isLoginView ? (
                  <>
                    {t('auth.variantB.heading.login')}{' '}
                    <span className="relative inline-block text-amber-600">
                      Logami
                      <svg viewBox="0 0 110 16" className="absolute -bottom-1 left-0 w-full h-3.5 pointer-events-none" preserveAspectRatio="none" aria-hidden>
                        <path d="M 3 11 C 20 5, 50 13, 75 7 C 92 4, 105 10, 107 8" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" opacity="0.55" />
                      </svg>
                    </span>
                  </>
                ) : (
                  <>
                    {t('auth.variantB.heading.signup')}<br />
                    <span className="relative inline-block text-amber-600">
                      {t('auth.variantB.heading.highlight')}
                      <svg viewBox="0 0 280 16" className="absolute -bottom-1 left-0 w-full h-3.5 pointer-events-none" preserveAspectRatio="none" aria-hidden>
                        <path d="M 4 11 C 40 5, 80 13, 130 7 C 180 3, 220 12, 268 7" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" opacity="0.55" />
                      </svg>
                    </span>
                  </>
                )}
              </h1>
              <p className="text-lg text-stone-600 leading-relaxed max-w-md">
                {isLoginView ? t('auth.variantB.description.login') : t('auth.variantB.description.signup')}
              </p>
            </div>

            {/* Feature list */}
            <div className="space-y-4">
              {vbFeatures.map((feat, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center mt-0.5">
                    <Check className="w-4 h-4 text-amber-700" />
                  </div>
                  <div>
                    <div className="font-semibold text-stone-900">{feat.title}</div>
                    <div className="text-sm text-stone-500">{feat.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Teacher avatar stack */}
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2.5">
                {TEACHERS_MINI.map((teacher, i) => (
                  teacher.photo ? (
                    <img
                      key={teacher.initials}
                      src={teacher.photo}
                      alt="Teacher"
                      className="w-9 h-9 rounded-full object-cover ring-2 ring-stone-50"
                      style={{ zIndex: TEACHERS_MINI.length - i }}
                    />
                  ) : (
                    <div
                      key={teacher.initials}
                      className="w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-semibold ring-2 ring-stone-50"
                      style={{ backgroundColor: teacher.color, color: '#44403C', zIndex: TEACHERS_MINI.length - i }}
                    >
                      {teacher.initials}
                    </div>
                  )
                ))}
              </div>
              <span className="text-sm font-medium text-stone-600">{t('auth.variantB.teacherLabel')}</span>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-stone-200">
              {vbStats.map((s, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl font-black text-stone-900 mb-1">{s.value}</div>
                  <div className="text-xs text-stone-500 font-medium">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Testimonial card */}
            <div className="bg-white border border-stone-200 rounded-2xl p-5 shadow-[0_2px_12px_rgba(28,25,23,0.06)]">
              <Quote className="w-5 h-5 text-amber-400 mb-3" />
              <p className="text-stone-700 text-sm leading-relaxed mb-4">
                &ldquo;{t('auth.variantB.testimonial.quote')}&rdquo;
              </p>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700 flex-shrink-0">SM</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-stone-800">{t('auth.variantB.testimonial.author')}</div>
                  <div className="text-xs text-stone-400">{t('auth.variantB.testimonial.details')}</div>
                </div>
                <span className="text-amber-400 text-xs font-bold flex-shrink-0">★★★★★</span>
              </div>
            </div>
          </div>

          {/* ── RIGHT FORM PANEL ──────────────────────────────── */}
          <div className="w-full">
            {/* Mobile social proof */}
            <div className="lg:hidden mb-5 text-center">
              <img src="/logo.png" alt="Logami" className="h-10 w-auto object-contain mx-auto mb-3" />
              <div className="flex items-center justify-center flex-wrap gap-x-3 gap-y-1 text-xs text-stone-500 font-medium">
                <span className="flex items-center gap-1"><span className="text-amber-500">★★★★★</span> 4.9/5</span>
                <span className="w-1 h-1 rounded-full bg-stone-300" />
                <span>1,000+ students</span>
                <span className="w-1 h-1 rounded-full bg-stone-300" />
                <span>95% pass rate</span>
              </div>
            </div>

            {/* Form card */}
            <div className="bg-white rounded-3xl border border-stone-200 shadow-[0_4px_32px_rgba(28,25,23,0.08)] p-7 sm:p-9">
              {/* Card heading */}
              <div className="text-center mb-7">
                <h2 className="font-space text-2xl font-black text-stone-900 mb-1.5">
                  {isLoginView ? t('auth.variantB.formTitle.login') : t('auth.variantB.formTitle.signup')}
                </h2>
                <p className="text-stone-500 text-sm">{t('auth.variantB.formSubtitle')}</p>
              </div>

              {/* Google. PRIMARY */}
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl border-2 border-stone-200 bg-white text-stone-800 font-semibold shadow-sm hover:border-amber-300 hover:bg-amber-50/40 hover:shadow-md focus:ring-2 focus:ring-amber-300 focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer min-h-[52px] touch-manipulation text-base"
              >
                <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                <span>{t('auth.variantB.googleButton')}</span>
              </button>

              {/* Security micro-copy */}
              <div className="flex items-center justify-center gap-3 mt-3 mb-5 text-[11px] text-stone-400">
                <span className="flex items-center gap-1">
                  <LockIcon className="w-3 h-3" />
                  {t('auth.variantB.trust.ssl')}
                </span>
                <span className="w-px h-3 bg-stone-200 rounded" />
                <span className="flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" />
                  {t('auth.variantB.trust.gdpr')}
                </span>
                <span className="w-px h-3 bg-stone-200 rounded" />
                <span>{t('auth.variantB.trust.free')}</span>
              </div>

              {/* OR divider */}
              <div className="relative flex items-center gap-3 mb-5">
                <div className="flex-1 h-px bg-stone-150 bg-stone-200" />
                <span className="text-xs font-medium text-stone-400">{t('auth.divider.orContinueWith')}</span>
                <div className="flex-1 h-px bg-stone-200" />
              </div>

              {/* Form, variant_b: email + password only (2 fields) */}
              <form className="space-y-4" onSubmit={isLoginView ? handleLogin : handleSignup}>
                {/* Email */}
                <div>
                  <label htmlFor="vb-email" className="block text-xs font-semibold text-stone-600 mb-1.5">
                    {t('auth.form.email')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <Mail className="h-4 w-4 text-stone-300" />
                    </div>
                    <input
                      id="vb-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full pl-10 pr-3.5 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-all bg-white text-stone-900 text-sm placeholder:text-stone-300"
                      placeholder={t('auth.placeholder.email')}
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label htmlFor="vb-password" className="block text-xs font-semibold text-stone-600">
                      {t('auth.form.password')}
                    </label>
                    {isLoginView && (
                      <button
                        type="button"
                        onClick={() => setShowPasswordReset(true)}
                        className="text-xs text-amber-600 hover:text-amber-800 font-medium transition-colors cursor-pointer"
                      >
                        {t('auth.resetPassword.forgotPassword')}
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                      <LockIcon className="h-4 w-4 text-stone-300" />
                    </div>
                    <input
                      id="vb-password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full pl-10 pr-10 py-3 border border-stone-200 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-all bg-white text-stone-900 text-sm placeholder:text-stone-300"
                      placeholder={isLoginView ? t('auth.placeholder.password.login') : t('auth.placeholder.password.signup')}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center cursor-pointer"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword
                        ? <EyeOff className="h-4 w-4 text-stone-300 hover:text-stone-500 transition-colors" />
                        : <Eye className="h-4 w-4 text-stone-300 hover:text-stone-500 transition-colors" />}
                    </button>
                  </div>
                  {/* Minimal password hint on signup */}
                  {!isLoginView && (
                    <p className="text-[11px] text-stone-400 mt-1.5">At least 6 characters</p>
                  )}
                </div>

                {/* Error */}
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-3.5 py-2.5 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                {/* Terms */}
                {!isLoginView && (
                  <div className="text-xs text-stone-400 text-center leading-relaxed">
                    {t('auth.consent.text')}{' '}
                    <button type="button" onClick={() => navigate('/terms')} className="text-stone-600 font-semibold underline cursor-pointer hover:text-amber-700 transition-colors">
                      {t('auth.consent.terms')}
                    </button>
                    {' '}{t('auth.consent.and')}{' '}
                    <button type="button" onClick={() => navigate('/privacy')} className="text-stone-600 font-semibold underline cursor-pointer hover:text-amber-700 transition-colors">
                      {t('auth.consent.privacy')}
                    </button>
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-stone-900 text-white py-3.5 px-4 rounded-xl font-space font-black text-base hover:bg-amber-700 focus:ring-4 focus:ring-amber-200 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer min-h-[52px] touch-manipulation"
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center gap-2">
                      <LogamiLogoSpinner size="md" decorative={false} alt="Logami loading" />
                      {isLoginView ? t('auth.button.signingIn') : t('auth.button.creatingAccount')}
                    </div>
                  ) : (
                    isLoginView ? t('auth.button.signIn') : t('auth.button.signUp')
                  )}
                </button>

                {/* Privacy micro-copy */}
                <div className="flex items-center justify-center gap-1.5 text-[11px] text-stone-400 pt-0.5">
                  <ShieldCheck className="w-3 h-3" />
                  <span>{t('auth.variantB.trust.privacy')}</span>
                </div>
              </form>

              {/* Toggle login/signup */}
              <div className="text-center pt-4 mt-1 border-t border-stone-100">
                <button
                  type="button"
                  onClick={toggleAuthMode}
                  className="text-stone-600 hover:text-amber-700 text-sm font-semibold transition-colors cursor-pointer"
                >
                  {isLoginView ? t('auth.toggle.toSignup') : t('auth.toggle.toLogin')}
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    );
  };

  // Animated features for left side
  const features = [
    {
      icon: Target,
      title: isLoginView ? t('auth.features.plan.title.login') : t('auth.features.plan.title.signup'),
      description: isLoginView ? t('auth.features.plan.desc.login') : t('auth.features.plan.desc.signup')
    },
    {
      icon: BookOpen,
      title: isLoginView ? t('auth.features.practice.title.login') : t('auth.features.practice.title.signup'),
      description: isLoginView ? t('auth.features.practice.desc.login') : t('auth.features.practice.desc.signup')
    },
    {
      icon: Trophy,
      title: isLoginView ? t('auth.features.pass.title.login') : t('auth.features.pass.title.signup'),
      description: isLoginView ? t('auth.features.pass.desc.login') : t('auth.features.pass.desc.signup')
    }
  ];

  const stats = [
    { icon: Users, value: '1,000+', label: t('auth.stats.activeUsers') },
    { icon: Award, value: '95%', label: t('auth.stats.successRate') },
    { icon: TrendingUp, value: '4.9/5', label: t('auth.stats.userRating') }
  ];

  const renderContent = () => {
    // Password Reset Confirmation View (when clicking link from email)
    if (isPasswordResetMode) {
      return (
        <div className="w-full max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center min-h-screen py-12">
            {/* LEFT SIDE */}
            <div className="hidden lg:flex flex-col justify-center space-y-12 pr-12">
              <div className="space-y-6">
                <div className="inline-block px-4 py-2 bg-black text-white text-xs font-bold tracking-widest uppercase rounded-full animate-pulse">
                  {t('auth.resetPassword.hero.badge')}
                </div>
                <h1 className="font-space text-5xl font-black text-black leading-tight">
                  {t('auth.resetPassword.hero.title')}<br />
                  <span className="bg-gradient-to-r from-black to-gray-600 bg-clip-text text-transparent">
                    {t('auth.resetPassword.hero.titleHighlight')}
                  </span>
                </h1>
                <p className="text-xl text-black/70 leading-relaxed">
                  {t('auth.resetPassword.confirm.description', 'Enter your new password below.')}
                </p>
              </div>
            </div>

            {/* RIGHT SIDE - Password Reset Confirmation Form */}
            <div className="w-full">
              <div className="relative bg-white rounded-3xl border-[3px] border-black shadow-2xl p-8 overflow-hidden">
                <div className="absolute top-0 left-0 w-20 h-20 border-t-4 border-l-4 border-black/10 rounded-tl-3xl" />
                <div className="absolute bottom-0 right-0 w-20 h-20 border-b-4 border-r-4 border-black/10 rounded-br-3xl" />
                <div className="relative z-10">
                  <div className="text-center mb-8">
                    <h2 className="font-space text-3xl font-black text-black mb-2">
                      {t('auth.resetPassword.confirm.title', 'Set New Password')}
                    </h2>
                    <p className="text-black/70">
                      {t('auth.resetPassword.confirm.subtitle', 'Enter your new password')}
                    </p>
                  </div>

                  {passwordResetSuccess ? (
                    <div className="space-y-6">
                      <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center">
                        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Lock className="w-8 h-8 text-green-600" />
                        </div>
                        <h3 className="font-bold text-lg text-green-800 mb-2">
                          {t('auth.resetPassword.confirm.success', 'Password Reset Successful!')}
                        </h3>
                        <p className="text-green-700 text-sm mb-4">
                          {t('auth.resetPassword.confirm.successDescription', 'Your password has been reset. You can now log in with your new password.')}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          setIsPasswordResetMode(false);
                          setPasswordResetSuccess(false);
                          setNewPassword('');
                          setConfirmNewPassword('');
                          setIsLoginView(true);
                          navigate('/auth');
                        }}
                        className="w-full bg-black text-white py-4 px-4 rounded-xl font-space font-black text-lg hover:bg-black/80 transition-all"
                      >
                        {t('auth.resetPassword.backToLogin')}
                      </button>
                    </div>
                  ) : (
                    <form className="space-y-5" onSubmit={handleConfirmPasswordReset}>
                      <div>
                        <label htmlFor="newPassword" className="block text-sm font-bold text-black mb-2">
                          {t('auth.resetPassword.confirm.newPassword', 'New Password')}
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Lock className="h-5 w-5 text-black/40" />
                          </div>
                          <input
                            id="newPassword"
                            type={showNewPassword ? 'text' : 'password'}
                            required
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="block w-full pl-12 pr-12 py-3 border-2 border-black/20 rounded-xl focus:ring-2 focus:ring-black focus:border-black transition-all bg-white font-medium"
                            placeholder={t('auth.placeholder.password.signup')}
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-4 flex items-center"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                          >
                            {showNewPassword ? <EyeOff className="h-5 w-5 text-black/40" /> : <Eye className="h-5 w-5 text-black/40" />}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="confirmNewPassword" className="block text-sm font-bold text-black mb-2">
                          {t('auth.form.confirmPassword')}
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Lock className="h-5 w-5 text-black/40" />
                          </div>
                          <input
                            id="confirmNewPassword"
                            type={showConfirmNewPassword ? 'text' : 'password'}
                            required
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            className="block w-full pl-12 pr-12 py-3 border-2 border-black/20 rounded-xl focus:ring-2 focus:ring-black focus:border-black transition-all bg-white font-medium"
                            placeholder={t('auth.placeholder.confirmPassword')}
                          />
                          <button
                            type="button"
                            className="absolute inset-y-0 right-0 pr-4 flex items-center"
                            onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                          >
                            {showConfirmNewPassword ? <EyeOff className="h-5 w-5 text-black/40" /> : <Eye className="h-5 w-5 text-black/40" />}
                          </button>
                        </div>
                      </div>

                      {error && (
                        <div className="bg-red-50 border-2 border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm font-medium">
                          {error}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-black text-white py-4 px-4 rounded-xl font-space font-black text-lg hover:bg-black/80 focus:ring-4 focus:ring-black/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {isLoading ? (
                          <div className="flex items-center justify-center">
                            <LogamiLogoSpinner size="md" className="mr-2" decorative={false} alt="Logami loading" />
                            {t('auth.resetPassword.confirm.resetting', 'Resetting...')}
                          </div>
                        ) : (
                          t('auth.resetPassword.confirm.submit', 'Reset Password')
                        )}
                      </button>

                      <div className="text-center pt-4">
                        <button
                          type="button"
                          onClick={() => {
                            setIsPasswordResetMode(false);
                            setResetOobCode('');
                            setNewPassword('');
                            setConfirmNewPassword('');
                            setIsLoginView(true);
                            navigate('/auth');
                          }}
                          className="text-black hover:text-black/70 text-sm font-bold transition-colors cursor-pointer"
                        >
                          {t('auth.resetPassword.backToLogin')}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Password Reset Request View
    if (showPasswordReset) {
      return (
        <div className="w-full max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center min-h-screen py-12">
            {/* LEFT SIDE - Same as login */}
            <div className="hidden lg:flex flex-col justify-center space-y-12 pr-12">
              <div className="space-y-6">
                <div className="inline-block px-4 py-2 bg-black text-white text-xs font-bold tracking-widest uppercase rounded-full animate-pulse">
                  {t('auth.resetPassword.hero.badge')}
                </div>
                <h1 className="font-space text-5xl font-black text-black leading-tight">
                  {t('auth.resetPassword.hero.title')}<br />
                  <span className="bg-gradient-to-r from-black to-gray-600 bg-clip-text text-transparent">
                    {t('auth.resetPassword.hero.titleHighlight')}
                  </span>
                </h1>
                <p className="text-xl text-black/70 leading-relaxed">
                  {t('auth.resetPassword.hero.description')}
                </p>
              </div>
            </div>

            {/* RIGHT SIDE - Password Reset Form */}
            <div className="w-full">
              <div className="relative bg-white rounded-3xl border-[3px] border-black shadow-2xl p-8 overflow-hidden">
                <div className="absolute top-0 left-0 w-20 h-20 border-t-4 border-l-4 border-black/10 rounded-tl-3xl" />
                <div className="absolute bottom-0 right-0 w-20 h-20 border-b-4 border-r-4 border-black/10 rounded-br-3xl" />
                <div className="relative z-10">
                  <div className="text-center mb-8">
                    <h2 className="font-space text-3xl font-black text-black mb-2">
                      {t('auth.resetPassword.title')}
                    </h2>
                    <p className="text-black/70">
                      {t('auth.resetPassword.subtitle')}
                    </p>
                  </div>

                  {resetEmailSent ? (
                    <div className="space-y-6">
                      <div className="bg-black/5 border-2 border-black/10 rounded-xl p-6 text-center">
                        <div className="w-16 h-16 bg-black/10 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Mail className="w-8 h-8 text-black" />
                        </div>
                        <h3 className="font-bold text-lg text-black mb-2">
                          {t('auth.resetPassword.emailSent')}
                        </h3>
                        <p className="text-black/70 text-sm mb-4">
                          {t('auth.resetPassword.emailSentDescription')}
                        </p>
                      </div>
                      <button
                        onClick={handleBackFromPasswordReset}
                        className="w-full bg-black text-white py-4 px-4 rounded-xl font-space font-black text-lg hover:bg-black/80 transition-all"
                      >
                        {t('auth.resetPassword.backToLogin')}
                      </button>
                    </div>
                  ) : (
                    <form className="space-y-5" onSubmit={handlePasswordReset}>
                      <div>
                        <label htmlFor="resetEmail" className="block text-sm font-bold text-black mb-2">
                          {t('auth.form.email')}
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Mail className="h-5 w-5 text-black/40" />
                          </div>
                          <input
                            id="resetEmail"
                            type="email"
                            required
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            className="block w-full pl-12 pr-4 py-3 border-2 border-black/20 rounded-xl focus:ring-2 focus:ring-black focus:border-black transition-all bg-white font-medium"
                            placeholder={t('auth.placeholder.email')}
                          />
                        </div>
                      </div>

                      {error && (
                        <div className="bg-red-50 border-2 border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm font-medium">
                          {error}
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-black text-white py-4 px-4 rounded-xl font-space font-black text-lg hover:bg-black/80 focus:ring-4 focus:ring-black/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {isLoading ? (
                          <div className="flex items-center justify-center">
                            <LogamiLogoSpinner size="md" className="mr-2" decorative={false} alt="Logami loading" />
                            {t('auth.resetPassword.sending')}
                          </div>
                        ) : (
                          t('auth.resetPassword.sendResetLink')
                        )}
                      </button>

                      <div className="text-center pt-4">
                        <button
                          type="button"
                          onClick={handleBackFromPasswordReset}
                          className="text-black hover:text-black/70 text-sm font-bold transition-colors cursor-pointer"
                        >
                          {t('auth.resetPassword.backToLogin')}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (isVariantB) return renderVariantBContent();

    return (
      <div className="w-full max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center min-h-screen py-12">
          {/* LEFT SIDE - Animated Content */}
          <div className="hidden lg:flex flex-col justify-center space-y-12 pr-12">
            {/* Hero Section */}
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-black text-white text-xs font-bold tracking-widest uppercase rounded-full animate-pulse">
                ✨ {isLoginView ? t('auth.hero.badge.login') : t('auth.hero.badge.signup')}
              </div>

              <h1 className="font-space text-5xl font-black text-black leading-tight">
                {isLoginView ? t('auth.hero.title.login') : t('auth.hero.title.signup')}<br />
                <span className="bg-gradient-to-r from-black to-gray-600 bg-clip-text text-transparent">
                  {t('auth.hero.titleHighlight')}
                </span>
              </h1>

              <p className="text-xl text-black/70 leading-relaxed">
                {isLoginView
                  ? t('auth.hero.description.login')
                  : t('auth.hero.description.signup')
                }
              </p>
            </div>

            {/* Animated Features */}
            <div className="space-y-6">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 group cursor-default"
                  style={{
                    animation: `slideInLeft 0.6s ease-out ${index * 0.2}s both`
                  }}
                >
                  <div className="relative flex-shrink-0">
                    <div className="absolute inset-0 bg-black rounded-xl blur-md opacity-0 group-hover:opacity-30 transition-opacity duration-500" />
                    <div className="relative w-14 h-14 bg-black rounded-xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                      <feature.icon className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-space text-lg font-black text-black mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-black/70">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t-2 border-black/10">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="text-center group cursor-default"
                  style={{
                    animation: `fadeIn 0.6s ease-out ${index * 0.15 + 0.8}s both`
                  }}
                >
                  <div className="inline-flex items-center justify-center w-10 h-10 bg-black/5 rounded-lg mb-3 group-hover:bg-black group-hover:scale-110 transition-all duration-300">
                    <stat.icon className="w-5 h-5 text-black group-hover:text-white" />
                  </div>
                  <div className="text-2xl font-black text-black mb-1">{stat.value}</div>
                  <div className="text-sm text-black/60 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>

          </div>

          {/* RIGHT SIDE - Auth Form */}
          <div className="w-full">
            {/* Mobile-only branding header */}
            <div className="lg:hidden text-center mb-6 px-2">
              <img src="/logo.png" alt="Logami" className="h-12 w-auto object-contain mx-auto mb-4" />
              <div className="flex items-center justify-center gap-4 text-xs text-slate-500 font-medium">
                <span className="flex items-center gap-1"><span className="text-amber-400">★★★★★</span> 4.9/5</span>
                <span className="w-1 h-1 rounded-full bg-slate-300" />
                <span>1,000+ students</span>
                <span className="w-1 h-1 rounded-full bg-slate-300" />
                <span>95% pass rate</span>
              </div>
            </div>
            <div className="relative bg-white rounded-3xl border border-slate-200 lg:border-[3px] lg:border-black shadow-xl lg:shadow-2xl p-6 sm:p-8 overflow-hidden">
              {/* Decorative corners */}
              <div className="absolute top-0 left-0 w-20 h-20 border-t-4 border-l-4 border-black/10 rounded-tl-3xl" />
              <div className="absolute bottom-0 right-0 w-20 h-20 border-b-4 border-r-4 border-black/10 rounded-br-3xl" />

              <div className="relative z-10">
                <div className="text-center mb-8">
                  <h2 className="font-space text-3xl font-black text-black mb-2">
                    {isLoginView ? t('auth.welcome.login') : t('auth.welcome.signup')}
                  </h2>
                  <p className="text-black/70">
                    {isLoginView ? t('auth.subtitle.login') : t('auth.subtitle.signup')}
                  </p>
                </div>

                {/* Google Sign-In, one tap for both login and sign up */}
                <div className="space-y-4">
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={isLoading}
                    className="w-full flex items-center justify-center gap-3 py-3.5 px-4 rounded-xl border-2 border-slate-300 bg-white text-slate-800 font-semibold shadow-sm hover:bg-slate-50 hover:border-slate-400 hover:shadow-md focus:ring-2 focus:ring-slate-300 focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer min-h-[52px] touch-manipulation text-base"
                  >
                    <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                    </svg>
                    <span>{t('auth.google.signIn')}</span>
                  </button>
                  <div className="relative flex items-center gap-3">
                    <div className="flex-1 h-px bg-black/15" />
                    <span className="text-sm font-medium text-black/60">{t('auth.divider.orContinueWith')}</span>
                    <div className="flex-1 h-px bg-black/15" />
                  </div>
                </div>

                {/* Mobile-only trust strip, social proof between Google and form */}
                {!isLoginView && (
                  <div className="lg:hidden flex items-center justify-center gap-3 py-2 px-3 rounded-xl bg-slate-50 border border-slate-100 mb-1">
                    <ShieldCheck className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                    <span className="text-xs text-slate-600 font-medium">Free to start · No credit card needed · Cancel anytime</span>
                  </div>
                )}

                <form className="space-y-5" onSubmit={isLoginView ? handleLogin : handleSignup}>
                  {/* Name Fields */}
                  {!isLoginView && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-bold text-black mb-2">
                          {t('auth.form.firstName')}
                        </label>
                        <input
                          id="firstName"
                          type="text"
                          required
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="block w-full px-4 py-3 border-2 border-black/20 rounded-xl focus:ring-2 focus:ring-black focus:border-black transition-all bg-white font-medium"
                          placeholder={t('auth.placeholder.firstName')}
                        />
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-bold text-black mb-2">
                          {t('auth.form.lastName')}
                        </label>
                        <input
                          id="lastName"
                          type="text"
                          required
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className="block w-full px-4 py-3 border-2 border-black/20 rounded-xl focus:ring-2 focus:ring-black focus:border-black transition-all bg-white font-medium"
                          placeholder={t('auth.placeholder.lastName')}
                        />
                      </div>
                    </div>
                  )}

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-sm font-bold text-black mb-2">
                      {t('auth.form.email')}
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-black/40" />
                      </div>
                      <input
                        id="email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full pl-12 pr-4 py-3 border-2 border-black/20 rounded-xl focus:ring-2 focus:ring-black focus:border-black transition-all bg-white font-medium"
                        placeholder={t('auth.placeholder.email')}
                      />
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="password" className="block text-sm font-bold text-black">
                        {t('auth.form.password')}
                      </label>
                      {isLoginView && (
                        <button
                          type="button"
                          onClick={() => setShowPasswordReset(true)}
                          className="text-sm text-black/70 hover:text-black font-medium transition-colors cursor-pointer"
                        >
                          {t('auth.resetPassword.forgotPassword')}
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Lock className="h-5 w-5 text-black/40" />
                      </div>
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="block w-full pl-12 pr-12 py-3 border-2 border-black/20 rounded-xl focus:ring-2 focus:ring-black focus:border-black transition-all bg-white font-medium"
                        placeholder={isLoginView ? t('auth.placeholder.password.login') : t('auth.placeholder.password.signup')}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-4 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5 text-black/40" /> : <Eye className="h-5 w-5 text-black/40" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  {!isLoginView && (
                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-bold text-black mb-2">
                        {t('auth.form.confirmPassword')}
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Lock className="h-5 w-5 text-black/40" />
                        </div>
                        <input
                          id="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          required
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="block w-full pl-12 pr-12 py-3 border-2 border-black/20 rounded-xl focus:ring-2 focus:ring-black focus:border-black transition-all bg-white font-medium"
                          placeholder={t('auth.placeholder.confirmPassword')}
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-4 flex items-center"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          {showConfirmPassword ? <EyeOff className="h-5 w-5 text-black/40" /> : <Eye className="h-5 w-5 text-black/40" />}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Error */}
                  {error && (
                    <div className="bg-red-50 border-2 border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm font-medium">
                      {error}
                    </div>
                  )}

                  {/* Terms */}
                  {!isLoginView && (
                    <div className="text-xs text-black/70 text-center">
                      {t('auth.consent.text')}{' '}
                      <button type="button" onClick={() => navigate('/terms')} className="text-black font-bold underline cursor-pointer">
                        {t('auth.consent.terms')}
                      </button>
                      {' '}{t('auth.consent.and')}{' '}
                      <button type="button" onClick={() => navigate('/privacy')} className="text-black font-bold underline cursor-pointer">
                        {t('auth.consent.privacy')}
                      </button>
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-black text-white py-4 px-4 rounded-xl font-space font-black text-lg hover:bg-black/80 focus:ring-4 focus:ring-black/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <LogamiLogoSpinner size="md" className="mr-2" decorative={false} alt="Logami loading" />
                        {isLoginView ? t('auth.button.signingIn') : t('auth.button.creatingAccount')}
                      </div>
                    ) : (
                      isLoginView ? t('auth.button.signIn') : t('auth.button.signUp')
                    )}
                  </button>

                  {/* Mobile security micro-copy */}
                  {!isLoginView && (
                    <div className="lg:hidden flex items-center justify-center gap-1.5 text-xs text-slate-400">
                      <ShieldCheck className="w-3.5 h-3.5" />
                      <span>Secure sign-up · Your data is encrypted</span>
                    </div>
                  )}

                  {/* Toggle */}
                  <div className="text-center pt-4">
                    <button
                      type="button"
                      onClick={toggleAuthMode}
                      className="text-black hover:text-black/70 text-sm font-bold transition-colors cursor-pointer"
                    >
                      {isLoginView ? t('auth.toggle.toSignup') : t('auth.toggle.toLogin')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen relative overflow-hidden ${isVariantB ? 'bg-[#FAFAF9]' : 'bg-gradient-to-b from-slate-50 to-white'}`}>
      {/* Back to Home, same style as other back buttons (arrow + text) */}
      <button
        onClick={() => navigate('/')}
        className={`absolute top-4 left-4 z-20 inline-flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-all cursor-pointer touch-manipulation min-h-[44px] ${isVariantB ? 'text-stone-600 hover:text-stone-900 hover:bg-stone-100' : 'text-black/70 hover:text-black hover:bg-black/5'}`}
        aria-label={t('auth.backToHome', 'Back to Home')}
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        {t('auth.backToHome', 'Back to Home')}
      </button>

      {renderContent()}

      {/* Keyframes for auth page animations (plain style - no jsx prop in Vite/React) */}
      <style>{`
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default AuthPage;